import React, {useEffect, useLayoutEffect, useState} from 'react';

/**
 * Хук для автоматического управления сворачиванием элементов фильтра.
 *
 * Определяет, сколько дочерних элементов помещаются в одну строку.
 * Если все элементы помещаются — кнопка "Показать ещё" не отображается.
 * Если не помещаются — скрывает лишние элементы и показывает кнопку "Показать ещё".
 *
 *   @returns {
 *     visibleCount: number — количество элементов, которые помещаются в одну строку,
 *     shouldCollapse: boolean — нужно ли показывать кнопку сворачивания,
 *     expanded: boolean — текущее состояние (развернуто/свернуто),
 *     setExpanded: (value: boolean) => void — ручной контроль,
 *     containerRef: RefObject<HTMLElement> — ref-контейнер, содержащий элементы
 *   }
 */
type UseAutoCollapseResult = {
    visibleCount: number
    shouldCollapse: boolean
    expanded: boolean
    setExpanded: (value: boolean) => void
    containerRef: React.RefObject<HTMLElement | null>
}

export default function useAutoCollapse({
                                            containerRef,
                                            filters
                                        }: {
    containerRef: React.RefObject<HTMLElement | null>;
    filters: any
}): UseAutoCollapseResult {
    const [visibleCount, setVisibleCount] = useState<number>(1);
    const [expanded, setExpanded] = useState(false);
    const [shouldCollapse, setShouldCollapse] = useState(false);

    const calculateVisibleCount = () => {
        const container = containerRef.current;
        if (!container) return;

        // Получаем computed style контейнера
        const computedStyle = window.getComputedStyle(container);
        const gapValue = computedStyle.columnGap;
        const containerWidth = container.clientWidth;

        const children = Array.from(container.children) as HTMLElement[];
        const totalCount = children.length;

        if (totalCount === 0) return;
        let count = 0;

        let placedInContainer = 0;

        // Пересчитываем, сколько элементов вмещается в контейнер
        for (let child of children) {
            const childWidth = child.getBoundingClientRect().width;

            const calculateItemChildWithGap = childWidth + parseFloat(gapValue);
            // Проверяем, помещается ли элемент в контейнер с учётом GAP
            if ((calculateItemChildWithGap + placedInContainer) <= containerWidth) {
                count++;
                placedInContainer += calculateItemChildWithGap;
            } else {
                break;
            }
        }
        count = Math.max(count, 1);
        setVisibleCount(count);
        setShouldCollapse(count < totalCount);
    };

    useEffect(() => {
        const container = containerRef.current;
        // Запускаем расчет сразу, если контейнер уже доступен
        if (container) {
            requestAnimationFrame(() => {
                calculateVisibleCount();
            });
        }

        // Подписка на MutationObserver для отслеживания изменений в DOM
        const observer = new MutationObserver(() => {
            if (container) {
                calculateVisibleCount();// Пересчитываем при изменении DOM
            }
        });

        if (container) {
            observer.observe(container, {
                childList: true, // Следим за добавлением/удалением дочерних элементов
                subtree: true,   // Следим за изменениями в поддереве
            });
        }

        // Очистка observer после размонтирования компонента
        return () => observer.disconnect();
    }, [containerRef]); // Зависимость от containerRef, чтобы пересчитывать на изменения

    // Подписка на resize события
    useLayoutEffect(() => {
        const handleResize = () => {
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    calculateVisibleCount();
                });
            });
        };

        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []); // Этот эффект сработает только один раз, при монтировании компонента;

    useEffect(() => {
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                calculateVisibleCount();
            });
        });
    }, [filters]); //Зависимость от filters, чтобы пересчитывать на изменения

    const setExpandedWithToggle = (value: boolean) => {
        setExpanded(value);
    };

    return {
        visibleCount,
        shouldCollapse,
        expanded,
        setExpanded: setExpandedWithToggle,
        containerRef,
    };
}
